// `window.__hangar` — the shell's own debug bridge.
//
// It exposes the kernel to first-party script on Hangar's origin. That is not a privilege
// boundary: anything reachable here is reachable anyway to code running in the shell, and an
// app frame is a *different* origin, so it cannot touch this (§9.4 — the origin is the
// boundary). §5.7 and §5.2's e2e budgets are written against it, and so is App Inspector.
import type { ExplorerShellAccess, SkinId } from '@hangar/shared';
import type { HangarKernel } from '@/kernel';
import { validateSkin, type SkinValidation } from '@/skins/contract';
import { SKINS, availableSkins } from '@/skins/registry';
import { createOpfsStore, type OpfsStore, type OpfsStoreOptions } from '@/kernel/vfs/opfs';
import { createExportService, type ExportService } from '@/kernel/vfs/export';
import { createExplorerAccess } from './capabilities';

export interface HangarDebugBridge {
  version: string;
  kernel: HangarKernel;
  /** The stable `.win-host` element for an instance key, or `null` (§5.7). */
  hostNode(instanceKey: string): HTMLElement | null;
  /** React commits recorded since boot, for the §20 performance budgets. */
  renderCount(): number;
  resetRenderCount(): void;
  /**
   * A second, independent §7.2 store over the same origin. The browser-integration specs use it
   * to exercise the `createWritable` fallback: Chromium always has the worker path, so without
   * a way to ask for a store *without* one, half of §7.2 would ship untested.
   */
  opfsStore(options?: OpfsStoreOptions): OpfsStore;
  /**
   * An export service with the worker forced off, for the same reason: §7.7's main-thread
   * fallback exists for browsers Chromium is not, and the two paths must agree.
   */
  exportService(options?: { useWorker?: boolean }): ExportService;
  /**
   * The D-059 capability the File Explorer holds. Exposed so its confinement can be attacked
   * directly — a capability that skipped `confine` would be a hole, and the only way to prove it
   * does not is to call it with a path Explorer may not reach (§9.5).
   */
  explorerAccess: ExplorerShellAccess;
  /**
   * §6.1 — the runtime validator, run against a registered skin. The conformance suite (M7.8)
   * parameterises itself over `registeredSkins()` and asserts this passes for each, which is how
   * D-048's "runs against every registered skin" becomes true rather than aspirational.
   */
  validateSkin(id: SkinId): SkinValidation;
  registeredSkins(): SkinId[];
}

declare global {
  interface Window {
    __hangar?: HangarDebugBridge;
  }
}

let renders = 0;

/** Called from a component render to record a React commit. */
export function noteRender(): void {
  renders += 1;
}

export function installDebugBridge(kernel: HangarKernel, version: string): void {
  window.__hangar = {
    version,
    kernel,
    hostNode(instanceKey) {
      return document.querySelector<HTMLElement>(
        `.win-host[data-instance-key="${CSS.escape(instanceKey)}"]`,
      );
    },
    renderCount: () => renders,
    resetRenderCount: () => {
      renders = 0;
    },
    explorerAccess: createExplorerAccess(kernel),
    validateSkin: (id) => {
      const skin = SKINS[id];
      return skin
        ? validateSkin(skin)
        : { ok: false, problems: [{ kind: 'id', name: id, message: `no skin '${id}' is registered` }] };
    },
    registeredSkins: () => availableSkins().map((skin) => skin.id),
    opfsStore: (options) => createOpfsStore(options ?? {}),
    exportService: (options) =>
      createExportService({ store: kernel.opfs, ...(options ?? {}) }),
  };
}
